import { cn } from "@/lib/utils";
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";

import "@/styles/task-kind.css";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  /** Accessible name of the radio group. */
  label: string;
  /** Glass track (mobile chrome) instead of the flat muted one. */
  glass?: boolean;
  size?: "sm" | "default";
  /**
   * Segments sized to their label (plus an equal share of the spare room)
   * instead of equal widths: lets long and short labels share a narrow track.
   */
  fit?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
}

/**
 * Segmented control with the navy pill sliding between equal segments on a
 * spring (see `src/styles/task-kind.css`). Keyboard model of a radio group:
 * arrows move and select, Home/End jump, only the checked segment is in the
 * tab order.
 */
export const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
  label,
  glass,
  size = "default",
  fit = false,
  className,
  id,
  "aria-describedby": describedBy,
}: SegmentedControlProps<T>) => {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const track = useRef<HTMLDivElement>(null);
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const fitIndicator = useFitIndicator(fit, track, buttons, index);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = options.length - 1;
    const keys: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = keys[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onChange(options[next].value);
    buttons.current[next]?.focus();
  };

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={label}
      aria-describedby={describedBy}
      onKeyDown={onKeyDown}
      data-size={size}
      className={cn(
        "segmented relative grid rounded-full p-1",
        glass ? "liquid-glass segmented--glass" : "segmented--flat",
        className,
      )}
      ref={track}
      style={{
        gridTemplateColumns: `repeat(${options.length}, ${fit ? "auto" : "1fr"})`,
      }}
    >
      <span
        aria-hidden
        className="segmented__indicator agenda-accent pointer-events-none absolute inset-y-1 left-0 rounded-full"
        style={
          fit
            ? (fitIndicator ?? { opacity: 0 })
            : {
                left: "0.25rem",
                width: `calc((100% - 0.5rem) / ${options.length})`,
                transform: `translateX(${index * 100}%)`,
              }
        }
      />
      {options.map((option, position) => {
        const active = position === index;
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[position] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.value)}
            data-active={active ? "true" : undefined}
            className="segmented__item relative z-10 flex min-w-0 items-center justify-center gap-1.5 rounded-full px-3 font-medium outline-none"
          >
            {option.icon}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/** Position of the sliding pill under the checked segment in `fit` mode. */
const useFitIndicator = (
  enabled: boolean,
  track: RefObject<HTMLDivElement | null>,
  buttons: RefObject<(HTMLButtonElement | null)[]>,
  index: number,
): CSSProperties | null => {
  const [style, setStyle] = useState<CSSProperties | null>(null);
  useLayoutEffect(() => {
    const node = track.current;
    if (!enabled || !node) return;
    const measure = () => {
      const button = buttons.current[index];
      if (!button) return;
      setStyle({
        width: button.offsetWidth,
        transform: `translateX(${button.offsetLeft}px)`,
      });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, track, buttons, index]);
  return style;
};
